import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormErrorMessage from './FormErrorMessage';
import FormInput from './FormInput';
import FormTextArea from './FormTextArea';
import SecondaryButtonBig from './SecondaryButtonBig';
import Spinner from './Spinner';
import withRecaptcha from './withRecaptcha';
import { useAlert } from '../context/AlertProvider';
import MyHead from './MyHead';
import FileInput from './FileInput';
import StyledImage from './StyledImage';
import { serialize } from 'object-to-formdata';
import logo from '../public/img/logo.svg';

interface IStoreClosedData {
  name: string;
  email: string;
  object: string;
  message: string;
  file: File;
  contact: boolean;
}

const StoreClosed: React.FC = () => {
  const alert = useAlert();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<IStoreClosedData>({
    defaultValues: {
      name: '',
      email: '',
      object: '',
      message: '',
      file: undefined,
      contact: false
    }
  });

  useEffect(() => {
    register('message', {
      required: t('REQUIRED_FIELD') as string
    });

    register('file');
  }, []);

  const onSubmit = async (values: IStoreClosedData, event: any) => {
    setLoading(true);

    const response = await fetch('/api/mailersend', {
      method: 'POST',
      body: serialize({
        email: values.email,
        fullname: values.name,
        subject: values.object,
        message: values.message,
        formName: 'store-closed',
        file: values.file,
        currentLanguage: i18n.language
      })
    });

    if (!response.ok) {
      alert.error(t('GENERIC_ERROR'));
      setLoading(false);
      return;
    }

    reset();
    event.target.reset();

    setLoading(false);
    alert.show(t('SEND_FORM_SUCCESS'));
  };

  const values = watch();

  return (
    <>
      <MyHead noindex />
      <div className="py-12 bg-gray-100 min-h-screen">
        <div className="max-w-5xl mx-auto px-6">
          <StyledImage src={logo} className="w-20 mb-6" alt="logo" />

          <p
            className="mb-4"
            dangerouslySetInnerHTML={{ __html: t('STORE_CLOSED_TITLE') }}
          />
          <p className="mb-8">{t('STORE_CLOSED_DESC')}</p>

          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <FormInput
                  id="name"
                  type="text"
                  placeholder={`${t('NAME')} *`}
                  invalid={Boolean(errors.name)}
                  {...{
                    ...register('name', {
                      required: t('REQUIRED_FIELD') as string
                    })
                  }}
                />

                <FormErrorMessage errors={errors} name="name" />
              </div>

              <div>
                <FormInput
                  id="email"
                  type="email"
                  placeholder={`${t('MAIL_ADDRESS')} *`}
                  invalid={Boolean(errors.email)}
                  {...register('email', {
                    required: t('REQUIRED_FIELD') as string,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                      message: t('INVALID_EMAIL')
                    }
                  })}
                />

                <FormErrorMessage errors={errors} name="email" />
              </div>

              <div>
                <FormInput
                  id="object"
                  type="text"
                  placeholder={`${t('SUBJECT')} *`}
                  invalid={Boolean(errors.object)}
                  {...{
                    ...register('object', {
                      required: t('REQUIRED_FIELD') as string
                    })
                  }}
                />

                <FormErrorMessage errors={errors} name="object" />
              </div>

              <div>
                <FormTextArea
                  id="message"
                  placeholder={`${t('MESSAGE')} *`}
                  invalid={Boolean(errors.message)}
                  value={values.message}
                  onChange={({ currentTarget }) =>
                    setValue('message', currentTarget.value, {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="message" />
              </div>

              <div>
                <FileInput
                  id="file"
                  label={t('STORE_CLOSED_FILE_LABEL')}
                  onChange={({ currentTarget }) =>
                    setValue('file', currentTarget.files![0], {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="file" />
              </div>

              <div
                className={`relative flex items-center mt-2 py-2 px-3 border rounded ${
                  errors.contact ? ' border-red-500' : 'border-transparent'
                }`}
              >
                <input
                  id="contact"
                  aria-describedby="privacy-description"
                  type="checkbox"
                  className="focus:outline-none h-4 w-4 text-primary border-gray-300 rounded"
                  {...register('contact', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />
                <div className="ml-2 text-sm">
                  <label htmlFor="contact">
                    <div
                      id="newsletter-description"
                      className="text-gray-500 cursor-pointer"
                    >
                      {t('FOOTER_NEWSLETTER_PRIVACY')} *{' '}
                      <a
                        href={`https://www.iubenda.com/privacy-policy/${t(
                          'IUBENDA.POLICY_ID'
                        )}`}
                        className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                        title="Privacy Policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('PRIVACY_TITLE')}
                      </a>
                    </div>
                  </label>
                </div>
              </div>
              <div className="mt-6 max-w-lg">
                {loading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="w-full">
                    <SecondaryButtonBig text={t('SEND')} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default withRecaptcha(StoreClosed);
